import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Box,
  Skeleton
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetCompanyByIdQuery } from "../../api/companyApi";
import { useGetBranchByIdQuery } from "../../api/branchApi";
import { useGetUserByIdQuery } from "../../api/userApi";
import { Collection } from "../../enums/collections.enum";
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser";

interface BreadcrumbItem {
  label: string;
  path?: string;
  isLoading?: boolean;
}

// Check if path is a Collection enum value
const isCollection = (path: string): boolean => {
  return Object.values(Collection).includes(path as Collection);
};

// Check if path looks like an ID (not a collection, not a keyword like 'edit', 'add')
const isId = (path: string): boolean => {
  if (isCollection(path)) return false;
  // ID patterns: UUIDs, alphanumeric strings, etc.
  return /^[0-9a-fA-F]{8,}$/.test(path) || /^[a-zA-Z0-9-_]{8,}$/.test(path);
};

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {isAdmin, currentUserIsOwnerOf} = useAuthenticatedUser();

  const paths = location.pathname.split("/").filter(Boolean);

  // Extract IDs from the URL path first (unconditionally)
  let companyId: string | undefined;
  let branchId: string | undefined;
  let userId: string | undefined;

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    if (isCollection(path)) {
      const nextPath = paths[i + 1];
      if (nextPath && isId(nextPath)) {
        if (path === Collection.Companies) {
          companyId = nextPath;
        } else if (path === Collection.Branches) {
          branchId = nextPath;
        } else if (path === Collection.Users) {
          userId = nextPath;
        }
        i++; // Skip the ID
      }
    }
  }

  // Call hooks unconditionally at the top level
  const { data: companyData, isLoading: companyLoading } = useGetCompanyByIdQuery(companyId || "", {
    skip: !companyId
  });
  const { data: branchData, isLoading: branchLoading } = useGetBranchByIdQuery(branchId || "", {
    skip: !branchId
  });
  const { data: employeeData, isLoading: employeeLoading } = useGetUserByIdQuery(userId || "", {
    skip: !userId
  });

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always add home
    breadcrumbs.push({ label: "Home", path: "/" });

    // Accumulate path parts for building full paths
    let accumulatedPath = "";

    // Iterate with explicit counter to look ahead
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      accumulatedPath += `/${path}`;

      // Check if this is a collection segment
      if (isCollection(path)) {
        const nextPath = paths[i + 1];

        if (nextPath && isId(nextPath)) {
          // Determine label and loading state based on collection type
          let label = nextPath;
          let isLoading = false;

          if (path === Collection.Companies) {
            label = companyData?.name || nextPath;
            isLoading = companyLoading;
          } else if (path === Collection.Branches) {
            label = branchData?.name || nextPath;
            isLoading = branchLoading;
          } else if (path === Collection.Users) {
            label = employeeData?.firstName || nextPath;
            isLoading = employeeLoading;
          }

          // Build full path including all accumulated segments
          const fullPath = `${accumulatedPath}/${nextPath}`;

          breadcrumbs.push({
            label,
            path: fullPath,
            isLoading
          });
          i++; // Skip the ID in next iteration and adjust acc path below
          accumulatedPath += `/${paths[i]}`;
        } else {
          // Collection without ID - this is a list view, skip it per requirements
          continue;
        }
      } else {
        // Skip other segments (edit, add, etc.)
        continue;
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page or if only home exists
  if (breadcrumbs.length <= 1) {
    return null;
  }

  // Check if any labels are still loading
  const isAnyLoading = breadcrumbs.some((b) => b.isLoading);

  if(!currentUserIsOwnerOf(companyId) && !isAdmin) {
    return <></>;
  }

  return (
    <Box sx={{ margin: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        {breadcrumbs.map((item, index) => {
          // const isLast = index === breadcrumbs.length - 1;

          if (isAnyLoading) {
            return (
              <Skeleton
                key={item.path || index}
                width={80}
                height={20}
                sx={{ display: "inline-block" }}
              />
            );
          }

          return (
            <Link
              key={item.path}
              component="button"
              onClick={() => item.path && navigate(item.path)}
              underline="hover"
              color="inherit"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" }
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;

